import { Link } from "react-router-dom";
import clsx from "clsx";
import { useId, useState } from "react";
import { Info } from "lucide-react";
import { planFeatures, planPrice, planPriceSub } from "../lib/pricing";

function Cta({ plan, cycle, featured, enterprise }) {
  const className = clsx(
    "block w-full rounded-lg px-4 py-2.5 text-center text-sm font-semibold transition",
    featured || enterprise
      ? "bg-pub-blue text-white shadow-sm hover:bg-pub-blue-dark"
      : "border border-pub-gray-200 bg-white text-pub-blue-dark hover:border-pub-blue hover:bg-pub-tint",
  );
  const href = plan.ctaUrl;
  if (href && /^https?:\/\//.test(href)) {
    return (
      <a href={href} className={className} target="_blank" rel="noreferrer">
        {plan.ctaLabel}
      </a>
    );
  }
  if (href && href.startsWith("mailto:")) {
    return (
      <a href={href} className={className}>
        {plan.ctaLabel}
      </a>
    );
  }
  // Carry the chosen plan + cycle into signup so it's preselected there.
  const signupHref = `/register?plan=${encodeURIComponent(plan.slug)}&cycle=${cycle}`;
  return (
    <Link to={href || signupHref} className={className}>
      {plan.ctaLabel}
    </Link>
  );
}

export default function PlanCard({ plan, cycle }) {
  const enterprise = plan.isCustomPricing;
  const featured = plan.isFeatured;
  const { amount, unit } = planPrice(plan, cycle);
  const [infoOpen, setInfoOpen] = useState(false);
  const tooltipId = useId();
  const signingAllowance =
    plan.signingDocumentLimit == null
      ? "Unlimited signature requests"
      : `${plan.signingDocumentLimit} signature requests per month`;

  return (
    <div
      className={clsx(
        "relative flex flex-col rounded-[10px] p-6",
        enterprise ? "bg-pub-navy text-white" : "border bg-white",
        !enterprise &&
          (featured
            ? "border-pub-blue shadow-pub-card-strong xl:-translate-y-1"
            : "border-pub-gray-200 shadow-pub-card"),
      )}
    >
      {plan.badge ? (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-pub-blue px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
          {plan.badge}
        </span>
      ) : null}

      <div className="relative flex items-center gap-1.5">
        <p
          className={clsx(
            "text-sm font-bold uppercase tracking-[0.08em]",
            enterprise ? "text-pub-tint" : "text-pub-blue-strong",
          )}
        >
          {plan.name}
        </p>
        <button
          type="button"
          aria-label={`About ${plan.name} plan`}
          aria-describedby={infoOpen ? tooltipId : undefined}
          className={clsx(
            "rounded-full p-1 focus:outline-none focus:ring-2 focus:ring-pub-blue",
            enterprise ? "text-white/70" : "text-pub-gray-500",
          )}
          onClick={() => setInfoOpen(true)}
          onMouseEnter={() => setInfoOpen(true)}
          onMouseLeave={() => setInfoOpen(false)}
          onFocus={() => setInfoOpen(true)}
          onBlur={() => setInfoOpen(false)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setInfoOpen(false);
          }}
        >
          <Info className="h-4 w-4" aria-hidden="true" />
        </button>
        {infoOpen ? (
          <div
            id={tooltipId}
            role="tooltip"
            className="absolute left-0 top-7 z-20 w-64 rounded-lg bg-pub-navy p-3 text-xs font-normal normal-case leading-5 tracking-normal text-white shadow-xl"
          >
            <p>{plan.pitch}</p>
            <p className="mt-1 font-semibold">{signingAllowance}</p>
            <p>
              {plan.fileConversationsEnabled === false
                ? "Conversations unavailable on this custom plan"
                : "MyDocUploads Conversations included"}
            </p>
          </div>
        ) : null}
      </div>
      <p
        className={clsx(
          "mt-1.5 min-h-9 text-[13px]",
          enterprise ? "text-white/70" : "text-pub-gray-600",
        )}
      >
        {plan.pitch}
      </p>

      <div className="mt-3 flex items-baseline gap-1">
        <span
          className={clsx(
            "font-extrabold tracking-tight",
            enterprise ? "text-[32px] text-white" : "text-[42px] text-pub-navy",
          )}
        >
          {amount}
        </span>
        {unit ? (
          <span className="text-sm text-pub-gray-500">{unit}</span>
        ) : null}
      </div>
      <p
        className={clsx(
          "mt-0.5 text-xs",
          enterprise ? "text-white/60" : "text-pub-gray-500",
        )}
      >
        {planPriceSub(plan, cycle)}
      </p>

      <div className="my-5">
        <Cta
          plan={plan}
          cycle={cycle}
          featured={featured}
          enterprise={enterprise}
        />
      </div>

      {planFeatures(plan).map((section) => (
        <div key={section.heading}>
          <p
            className={clsx(
              "mb-2 border-t pt-3 text-[11px] font-bold uppercase tracking-[0.08em]",
              enterprise
                ? "border-white/15 text-white/50"
                : "border-pub-gray-200 text-pub-gray-500",
            )}
          >
            {section.heading}
          </p>
          <ul className="mb-3">
            {(section.items ?? []).map((item) => (
              <li
                key={item.text}
                className={clsx(
                  "flex gap-2.5 py-1 text-[13.5px]",
                  item.muted
                    ? enterprise
                      ? "text-white/50"
                      : "text-pub-gray-500"
                    : enterprise
                      ? "text-white/85"
                      : "text-pub-gray-700",
                )}
              >
                {!item.muted && (
                  <span className="shrink-0 font-extrabold text-pub-blue">
                    ✓
                  </span>
                )}
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
